import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, KeyRound, ArrowLeft, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { MotorInksLogo } from '@/components/Brand';
import { TID } from '@/testIds';

export default function LoginPage() {
  const { requestCode, verifyCode } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get('next') || '/dashboard';

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState(null);

  const doRequest = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const r = await requestCode(email);
      setRole(r.role);
      setStep('code');
      toast.success(`Sign-in code sent to ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send code');
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    if (!code) return;
    setBusy(true);
    try {
      const s = await verifyCode(email, code);
      toast.success(`Signed in as ${s.user.role}`);
      // Route by role: admin can hit /admin, partners → /dashboard
      let target = nextPath;
      if (target === '/admin' && s.user.role !== 'admin') target = '/dashboard';
      nav(target, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="mi-eyebrow text-white/50 hover:text-white inline-flex items-center gap-2 mb-8">
          <ArrowLeft className="w-3 h-3" /> BACK TO HOME
        </Link>

        <div className="flex justify-center mb-6">
          <MotorInksLogo variant="primary" height={64}
                         className="drop-shadow-[0_4px_16px_rgba(230,57,70,0.3)]" />
        </div>

        <div className="mi-surface p-8 lg:p-10 border-t-4 border-t-[#E63946]">
          <div className="mi-eyebrow text-[#E63946] mb-3">
            {step === 'email' ? '// SIGN IN' : `// CODE SENT · ${role === 'admin' ? 'ADMIN' : 'PARTNER'}`}
          </div>
          <h1 className="mi-headline text-4xl leading-tight mb-2">
            {step === 'email' ? (<>Access your <span className="text-[#E63946]">dashboard.</span></>)
                              : (<>Enter your <span className="text-[#E63946]">6-digit code.</span></>)}
          </h1>
          <p className="text-white/60 text-sm mb-6">
            {step === 'email'
              ? 'We\'ll email you a one-time code — no password required.'
              : `We sent a code to ${email}. Codes expire in 10 minutes.`}
          </p>

          {step === 'email' ? (
            <form onSubmit={doRequest} className="space-y-4">
              <div>
                <div className="mi-eyebrow mb-2 text-white/70">Email address</div>
                <div className="relative">
                  <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    required autoFocus type="email"
                    placeholder="you@shop.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid={TID.authEmailInput}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm pl-10 pr-4 py-3 text-white text-sm placeholder-white/40 hover:border-white/25"
                  />
                </div>
              </div>
              <button
                type="submit" disabled={busy || !email}
                data-testid={TID.authRequestBtn}
                className="mi-btn-red w-full disabled:opacity-60"
              >
                {busy ? 'Sending code…' : (<>Send sign-in code <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          ) : (
            <form onSubmit={doVerify} className="space-y-4">
              <div>
                <div className="mi-eyebrow mb-2 text-white/70">6-digit code</div>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    required autoFocus type="text"
                    inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                    placeholder="000000"
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    data-testid={TID.authCodeInput}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm pl-10 pr-4 py-3 text-white text-2xl font-mono tracking-[0.5em] placeholder-white/25 hover:border-white/25"
                  />
                </div>
              </div>
              <button
                type="submit" disabled={busy || code.length !== 6}
                data-testid={TID.authVerifyBtn}
                className="mi-btn-red w-full disabled:opacity-60"
              >
                {busy ? 'Verifying…' : (<>Verify &amp; sign in <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); }}
                className="w-full text-white/50 hover:text-white text-xs mi-eyebrow"
              >
                ← USE A DIFFERENT EMAIL
              </button>
            </form>
          )}
        </div>

        <div className="mt-6 text-center mi-eyebrow text-white/40 text-[10px]">
          NEED HELP? EMAIL SALES@MOTORINKS.COM · 832.791.2692
        </div>
      </div>
    </div>
  );
}
